/**
 * Offline queue for run actions (step ticks, finish, abandon).
 *
 * Every action is written to localStorage first (`procedures.queue`) and the screen updates straight
 * away from that copy; the sync engine (components/sync/SyncEngine.tsx) then sends the queue in order,
 * retrying with backoff while there is no signal. Ticks of the same step collapse (the latest wins) and
 * carry `clientCheckedAt`, so the server records when the step was really done.
 */
import { create } from 'zustand';
import type { ProcedureRunFinishRequest, ProcedureRunStepUpdateRequest } from '@actschurch/ccms-sdk';
import { uuid } from '@/utils/uuid';
import type { PortalMode } from './queryKeys';
import { readJson, writeJson } from './storage';

const QUEUE_KEY = 'procedures.queue';

export type QueueKind = 'step' | 'complete' | 'abandon';

export interface QueueEntry {
  id: string;
  runId: number;
  code: string;
  mode: PortalMode;
  kind: QueueKind;
  stepId?: number;
  step?: ProcedureRunStepUpdateRequest;
  finish?: ProcedureRunFinishRequest;
  /** When the person did it on the phone (ms). */
  createdAt: number;
  attempts: number;
  /** Not before this time (ms); backoff after a failed try. */
  nextAttemptAt: number;
  lastError?: string | null;
}

export interface SyncProblem {
  runId: number;
  code: string;
  message: string;
  at: number;
}

interface SyncState {
  entries: QueueEntry[];
  /** The entry being sent right now (never collapsed or removed underneath the request). */
  inFlightId: string | null;
  online: boolean;
  lastSyncedAt: number | null;
  /** The last action the server refused (not retried). Shown once, then cleared. */
  problem: SyncProblem | null;
  /** Bumped to wake the engine up (new entry, back online). */
  wake: number;
}

function load(): QueueEntry[] {
  const list = readJson<QueueEntry[]>(QUEUE_KEY);
  return Array.isArray(list) ? list.filter((e) => e && typeof e.runId === 'number' && typeof e.kind === 'string') : [];
}

function persist(entries: QueueEntry[]): void {
  writeJson(QUEUE_KEY, entries);
}

export const useSyncStore = create<SyncState>(() => ({
  entries: typeof window === 'undefined' ? [] : load(),
  inFlightId: null,
  online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  lastSyncedAt: null,
  problem: null,
  wake: 0,
}));

function setEntries(next: QueueEntry[]): void {
  persist(next);
  useSyncStore.setState((s) => ({ entries: next, wake: s.wake + 1 }));
}

/** Queue a step tick / skip / problem / untick. Replaces an earlier unsent tick of the same step. */
export function enqueueStep(args: {
  runId: number;
  code: string;
  mode: PortalMode;
  stepId: number;
  request: ProcedureRunStepUpdateRequest;
}): void {
  const { entries, inFlightId } = useSyncStore.getState();
  const kept = entries.filter(
    (e) => !(e.kind === 'step' && e.runId === args.runId && e.stepId === args.stepId && e.id !== inFlightId),
  );
  const now = Date.now();
  kept.push({
    id: uuid(),
    runId: args.runId,
    code: args.code,
    mode: args.mode,
    kind: 'step',
    stepId: args.stepId,
    step: args.request,
    createdAt: now,
    attempts: 0,
    nextAttemptAt: now,
  });
  setEntries(kept);
}

/** Queue finishing (complete or abandon) a run. Sent after its step ticks. */
export function enqueueFinish(args: {
  runId: number;
  code: string;
  mode: PortalMode;
  kind: 'complete' | 'abandon';
  request?: ProcedureRunFinishRequest;
}): void {
  const { entries } = useSyncStore.getState();
  const now = Date.now();
  const kept = entries.filter((e) => !(e.runId === args.runId && (e.kind === 'complete' || e.kind === 'abandon')));
  kept.push({
    id: uuid(),
    runId: args.runId,
    code: args.code,
    mode: args.mode,
    kind: args.kind,
    finish: args.request ?? {},
    createdAt: now,
    attempts: 0,
    nextAttemptAt: now,
  });
  setEntries(kept);
}

export function removeEntry(id: string): void {
  setEntries(useSyncStore.getState().entries.filter((e) => e.id !== id));
}

/** Drop everything queued for a run (it closed, or its token is gone). */
export function dropRun(runId: number): void {
  setEntries(useSyncStore.getState().entries.filter((e) => e.runId !== runId));
}

export function patchEntry(id: string, patch: Partial<QueueEntry>): void {
  setEntries(useSyncStore.getState().entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
}

export function setInFlight(id: string | null): void {
  useSyncStore.setState({ inFlightId: id });
}

export function setOnline(online: boolean): void {
  useSyncStore.setState((s) => ({ online, wake: online ? s.wake + 1 : s.wake }));
}

export function markSynced(): void {
  useSyncStore.setState({ lastSyncedAt: Date.now() });
}

export function reportProblem(problem: Omit<SyncProblem, 'at'>): void {
  useSyncStore.setState({ problem: { ...problem, at: Date.now() } });
}

export function clearProblem(): void {
  useSyncStore.setState({ problem: null });
}

/** Wake the engine (e.g. after signing in, so signed-in entries can go). */
export function wakeSync(): void {
  useSyncStore.setState((s) => ({ wake: s.wake + 1 }));
}

/** Pick up writes made in another tab of this portal. */
export function reloadFromStorage(): void {
  useSyncStore.setState((s) => ({ entries: load(), wake: s.wake + 1 }));
}

/** Entries waiting for this run (oldest first). */
export function useRunPending(runId: number | null): QueueEntry[] {
  return useSyncStore((s) => (runId ? s.entries.filter((e) => e.runId === runId) : EMPTY));
}
const EMPTY: QueueEntry[] = [];

/** Backoff: 2 s, 4 s, 8 s ... up to 60 s. */
export function backoffMs(attempts: number): number {
  return Math.min(60_000, 2_000 * 2 ** Math.max(0, attempts - 1));
}
